import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, Shield, ArrowLeft, Mail } from '../../icons/wp-icons';
import { useSuperAdmin } from '../../context/SuperAdminContext';

const SuperAdminLogin = () => {
  const [username, setUsername] = useState('');
  const [passwort, setPasswort] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  const totpInputRef = useRef(null);
  const emailCodeInputRef = useRef(null);
  const navigate = useNavigate();
  const { login, verify2FA, verify3FA, cancel2FA, pending2FA, pending3FA, isAuthenticated, loading: authLoading } = useSuperAdmin();

  // Wenn bereits eingeloggt, zum Dashboard weiterleiten
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/super-admin/dashboard');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Fokus auf TOTP-Eingabe wenn 2FA erforderlich
  useEffect(() => {
    if (pending2FA && totpInputRef.current) {
      totpInputRef.current.focus();
    }
  }, [pending2FA]);

  // Fokus auf Email-Code-Eingabe wenn 3FA erforderlich
  useEffect(() => {
    if (pending3FA && emailCodeInputRef.current) {
      emailCodeInputRef.current.focus();
    }
  }, [pending3FA]);

  // Auth Loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-500 border-t-transparent"></div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(username, passwort);

    if (result.success) {
      if (result.requires3FA) {
        setMaskedEmail(result.maskedEmail || '');
      } else if (!result.requires2FA) {
        navigate('/super-admin/dashboard');
      }
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await verify2FA(totpCode);

    if (result.success) {
      if (result.requires3FA) {
        // Nach 2FA kommt noch 3FA
        setMaskedEmail(result.maskedEmail || '');
        setTotpCode('');
      } else {
        if (result.usedBackupCode) {
          alert('Sie haben einen Backup-Code verwendet. Bitte generieren Sie neue Backup-Codes in den Einstellungen.');
        }
        navigate('/super-admin/dashboard');
      }
    } else {
      setError(result.error);
      setTotpCode('');
    }

    setLoading(false);
  };

  const handle3FASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await verify3FA(emailCode);

    if (result.success) {
      navigate('/super-admin/dashboard');
    } else {
      setError(result.error);
      setEmailCode('');
    }

    setLoading(false);
  };

  const handleCancel = () => {
    cancel2FA();
    setTotpCode('');
    setEmailCode('');
    setMaskedEmail('');
    setError('');
  };

  // TOTP-Eingabe automatisch formatieren (nur Zahlen)
  const handleTotpChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 8);
    setTotpCode(value);
  };

  const handleEmailCodeChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
    setEmailCode(value);
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo / Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 mb-4">
            <img src="/favicon.png" alt="Logo" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-2xl font-bold text-white">Super-Admin</h1>
          <p className="text-gray-400 mt-2">
            {pending3FA
              ? 'E-Mail-Verifizierung'
              : pending2FA
                ? 'Zwei-Faktor-Authentifizierung'
                : 'Systemadministrator-Zugang'}
          </p>
        </div>

        {/* 3FA Verification Form (Email-OTP) */}
        {pending3FA ? (
          <form onSubmit={handle3FASubmit} className="bg-gray-800 rounded-xl p-6 shadow-xl">
            <div className="flex items-center justify-center mb-6">
              <div className="p-4 bg-blue-600/20 rounded-full">
                <Mail className="w-8 h-8 text-blue-400" />
              </div>
            </div>

            <p className="text-center text-gray-300 mb-2">
              Ein 6-stelliger Sicherheitscode wurde gesendet an:
            </p>
            <p className="text-center text-white font-medium mb-6">
              {maskedEmail}
            </p>

            {error && (
              <div className="mb-4 p-3 bg-red-900/50 border border-red-500 rounded-lg flex items-center gap-2 text-red-200">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="mb-6">
              <input
                ref={emailCodeInputRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={emailCode}
                onChange={handleEmailCodeChange}
                className="w-full px-4 py-4 bg-gray-700 border border-gray-600 rounded-lg text-white text-center text-2xl tracking-widest font-mono placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="000000"
                maxLength={6}
                required
              />
              <p className="text-center text-gray-500 text-sm mt-2">
                Der Code ist 5 Minuten gültig
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || emailCode.length < 6}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></span>
                  Verifizieren...
                </>
              ) : (
                'Verifizieren'
              )}
            </button>

            <button
              type="button"
              onClick={handleCancel}
              className="w-full mt-3 py-3 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück zur Anmeldung
            </button>
          </form>
        ) : pending2FA ? (
          /* 2FA Verification Form */
          <form onSubmit={handle2FASubmit} className="bg-gray-800 rounded-xl p-6 shadow-xl">
            <div className="flex items-center justify-center mb-6">
              <div className="p-4 bg-red-600/20 rounded-full">
                <Shield className="w-8 h-8 text-red-400" />
              </div>
            </div>

            <p className="text-center text-gray-300 mb-6">
              Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein
            </p>

            {error && (
              <div className="mb-4 p-3 bg-red-900/50 border border-red-500 rounded-lg flex items-center gap-2 text-red-200">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="mb-6">
              <input
                ref={totpInputRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={totpCode}
                onChange={handleTotpChange}
                className="w-full px-4 py-4 bg-gray-700 border border-gray-600 rounded-lg text-white text-center text-2xl tracking-widest font-mono placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="000000"
                maxLength={8}
                required
              />
              <p className="text-center text-gray-500 text-sm mt-2">
                Oder geben Sie einen Backup-Code ein
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || totpCode.length < 6}
              className="w-full py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></span>
                  Verifizieren...
                </>
              ) : (
                'Verifizieren'
              )}
            </button>

            <button
              type="button"
              onClick={handleCancel}
              className="w-full mt-3 py-3 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück zur Anmeldung
            </button>
          </form>
        ) : (
          /* Login Form */
          <form onSubmit={handleSubmit} className="bg-gray-800 rounded-xl p-6 shadow-xl">
            {error && (
              <div className="mb-4 p-3 bg-red-900/50 border border-red-500 rounded-lg flex items-center gap-2 text-red-200">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Benutzername
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Benutzername eingeben"
                required
                autoComplete="username"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Passwort
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={passwort}
                  onChange={(e) => setPasswort(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent pr-12"
                  placeholder="Passwort eingeben"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></span>
                  Anmelden...
                </>
              ) : (
                'Anmelden'
              )}
            </button>
          </form>
        )}

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-6">
          Dieser Bereich ist nur für Systemadministratoren.
        </p>
      </div>
    </div>
  );
};

export default SuperAdminLogin;
